import React, { useMemo, useRef, useState } from "react";
import { useLocalization } from '@progress/kendo-react-intl';
import { enMessages } from "../../../../../messages/en-US";
import moment from "moment";
import { SortDescriptor, orderBy } from "@progress/kendo-data-query";
import { ExcelExport, ExcelExportColumn } from "@progress/kendo-react-excel-export";
import { Grid, GridColumn, GridCustomCellProps, GridSortChangeEvent, GridToolbar } from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";
import { downloadIcon } from "@progress/kendo-svg-icons";
import LocoFuelBurnReadDto from "../../../../../Models/Dashboard/LocoFuelBurnReadDto";

interface Props {
  data: LocoFuelBurnReadDto[] | null | undefined;
  isFullscreen?: boolean;
  isLoading?: boolean;
}

export const FuelBurnTableComponent: React.FC<Props> = ({ data, isFullscreen = false, isLoading = false }) => {
  const [sort, setSort] = useState<SortDescriptor[]>([{ field: 'timestamp', dir: 'desc' }]);
  const _export = useRef<ExcelExport | null>(null);
  const localizationService = useLocalization();

  const excelExport = () => {
    if (_export.current !== null) {
      _export.current.save();
    }
  };

  // Process data with sorting - data already comes in the correct format
  const processedData = useMemo(() => {
    if (!data || !Array.isArray(data) || data.length === 0) return [];
    
    if (sort && sort.length > 0) {
      return orderBy(data, sort);
    }
    
    return data;
  }, [data, sort]);

  // Prepare export data with formatted fields
  const exportData = useMemo(() => {
    return processedData.map((item) => ({
      serialNo: item.serialNo || '-',
      timestampExport: moment(item.timestamp).format('DD/MM/YYYY HH:mm:ss'),
      syncedExport: moment(item.synced).format('DD/MM/YYYY HH:mm:ss'),
      firmwareVersion: item.firmwareVersion || '-',
      deviceId: item.deviceId || '-',
      speed: item.speed !== null && item.speed !== undefined ? item.speed.toFixed(2) : '-',
      fuelBurnRate: item.fuelBurnRateTempComp !== null && item.fuelBurnRateTempComp !== undefined ? item.fuelBurnRateTempComp.toFixed(2) : '-'
    }));
  }, [processedData]);

  const TimestampCell = (props: GridCustomCellProps) => {
    return (
      <td>
        <div>{moment(props.dataItem.timestamp).format('DD/MM/YYYY @ HH:mm:ss')}</div>
      </td>
    );
  };

  const SyncedCell = (props: GridCustomCellProps) => {
    return (
      <td>
        <div>{moment(props.dataItem.synced).format('DD/MM/YYYY @ HH:mm:ss')}</div>
      </td>
    );
  };

  const SpeedCell = (props: GridCustomCellProps) => {
    const value = props.dataItem.speed;
    const displayValue = value !== null && value !== undefined ? value.toFixed(2) : '-';
    return (
      <td>
        <div>{displayValue}</div>
      </td>
    );
  };

  const FuelBurnRateCell = (props: GridCustomCellProps) => {
    const value = props.dataItem.fuelBurnRateTempComp;
    const displayValue = value !== null && value !== undefined ? value.toFixed(2) : '-';
    return (
      <td>
        <div>{displayValue}</div>
      </td>
    );
  };

  const height = isFullscreen ? '100%' : 'calc(100vh - 420px)';

  if (isLoading) {
    return (
      <div className="w-full flex items-center justify-center" style={{ height }}>
        <div className="flex flex-col items-center gap-2">
          <div>Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <ExcelExport data={exportData} ref={_export} fileName="Fuel Burn Data.xlsx">
        <ExcelExportColumn field="serialNo" title={localizationService.toLanguageString('custom.serialNumber', enMessages.custom.serialNumber)} />
        <ExcelExportColumn field="timestampExport" title={localizationService.toLanguageString('custom.occurred', enMessages.custom.occurred)} width={150} />
        <ExcelExportColumn field="syncedExport" title={localizationService.toLanguageString('custom.uploaded', enMessages.custom.uploaded)} width={150} />
        <ExcelExportColumn field="firmwareVersion" title={localizationService.toLanguageString('custom.firmwareVersion', enMessages.custom.firmwareVersion)} />
        <ExcelExportColumn field="deviceId" title="Device ID" />
        <ExcelExportColumn field="speed" title="Speed (km/h)" />
        <ExcelExportColumn field="fuelBurnRate" title="Fuel Burn Rate (L/hr)" />
      </ExcelExport>
      
      <Grid 
        data={processedData} 
        style={{ flex: 1, minHeight: 0 }}
        sortable={true}
        sort={sort}
        onSortChange={(e: GridSortChangeEvent) => {
          setSort(e.sort);
        }}
      >
        <GridToolbar>
          <div className="flex justify-end items-center w-full">
            <div className="flex gap-2">
              <Button
                themeColor="base"
                title={localizationService.toLanguageString('custom.exportToExcel', enMessages.custom.exportToExcel)}
                onClick={excelExport}
                svgIcon={downloadIcon}
                disabled={processedData.length === 0}
              />
            </div>
          </div>
        </GridToolbar>
        
        <GridColumn 
          field="serialNo" 
          title={localizationService.toLanguageString('custom.serialNumber', enMessages.custom.serialNumber)} 
        />
        <GridColumn 
          field="timestamp" 
          title={localizationService.toLanguageString('custom.occurred', enMessages.custom.occurred)} 
          cells={{ data: TimestampCell }} 
        />
        <GridColumn 
          field="synced" 
          title={localizationService.toLanguageString('custom.uploaded', enMessages.custom.uploaded)} 
          cells={{ data: SyncedCell }} 
        />
        <GridColumn 
          field="firmwareVersion" 
          title={localizationService.toLanguageString('custom.firmwareVersion', enMessages.custom.firmwareVersion)} 
        />
        <GridColumn 
          field="deviceId" 
          title="Device ID" 
        />
        <GridColumn 
          field="speed" 
          title="Speed (km/h)" 
          cells={{ data: SpeedCell }} 
        />
        <GridColumn 
          field="fuelBurnRateTempComp" 
          title="Fuel Burn Rate (L/hr)" 
          cells={{ data: FuelBurnRateCell }} 
        />
      </Grid>
    </div>
  );
};
